'use client';
import { useQuery } from '@apollo/client/react';
import {
  DndContext,
  MouseSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useDropzone } from 'react-dropzone';

import { useState } from 'react';

import { graphql } from '@/gql';

import type { FileManagerUploadingState } from '@/modules/files/components/file-manager/FileManagerUploadingBar';

import type { FileRowFileFieldsFragment } from '@/gql/graphql';

import { useFileUploader } from '../hooks/useFileUploader';

import { FileManagerUploadingBar } from './file-manager/FileManagerUploadingBar';
import { FileManagerToolbar } from './file-manager/FileManagerToolbar';
import { FileManagerContent } from './file-manager/FileManagerContent';
import type { Segment } from './file-manager/toolbar/breadcrumbs-bar/FileManagerBreadcrumbsBarSegment';
import type { ChangeOwnerDialogProps } from './file-manager/actions/move/FileManagerChangeOwnerConfirmDialog';
import { FileManagerChangeOwnerConfirmDialog } from './file-manager/actions/move/FileManagerChangeOwnerConfirmDialog';

import type { OwnerCandidate } from './file-manager/content/file-row/FileOwnerSelector';
import type { ConfirmFileMoveDialogProps } from './file-manager/dialogs/ConfirmFileMoveDialog';
import { ConfirmFileMoveDialog } from './file-manager/dialogs/ConfirmFileMoveDialog';

export const FILE_MANAGER_QUERY = graphql(`
  query FileManager(
    $rootOwnerId: ID!
    $fileId: ID
    $includeSharingGroups: Boolean!
  ) {
    groupsForSharing(item: { id: $rootOwnerId, type: "Service" })
      @include(if: $includeSharingGroups) {
      edges {
        node {
          _id
          name
        }
      }
    }
    filesForOwner(
      ownerId: $rootOwnerId
      parentId: $fileId
      includeChildren: true
    ) {
      ...fileManagerToolbarFilesForOwnerFields
      ...fileManagerUploadingBarFilesForOwnerFields
      ...fileManagerContentFields
      relatedOwners {
        owner {
          ... on Group {
            _id
          }

          ... on Service {
            _id
          }
        }
        userCanEditFiles
        userCanLockFiles
        userCanPreventDownloadOfFiles
      }
    }
  }
`);

interface Props {
  rootOwnerType: 'Group' | 'Service';
  rootOwnerId: string | null | undefined;
  fileId: string | undefined;
  sort: string | undefined;
}

export const FileManager = ({
  rootOwnerType,
  rootOwnerId,
  fileId,
  sort,
}: Props) => {
  const [searchMode, setSearchMode] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [uploadingState, setUploadingState] =
    useState<FileManagerUploadingState>({
      uploadingFiles: [],
      uploadingFolders: [],
      uploadingZipFiles: [],
    });
  const [changeOwnerDialogOpen, setChangeOwnerDialogOpen] =
    useState<ChangeOwnerDialogProps>();
  const [confirmMoveDialogOpen, setConfirmMoveDialogOpen] =
    useState<ConfirmFileMoveDialogProps>();

  const { loading, data } = useQuery(FILE_MANAGER_QUERY, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    variables: {
      rootOwnerId: rootOwnerId ?? '',
      fileId: fileId ?? null,
      includeSharingGroups: Boolean(rootOwnerType === 'Service'),
    },
    skip: !rootOwnerId,
  });

  const { filesForOwner, groupsForSharing } = data ?? {};
  const { parent, owner, relatedOwners } = filesForOwner ?? {};

  const breadcrumbs = parent?.breadcrumbs?.map((crumb) => crumb.file) ?? [];

  if (!searchText && parent && !parent._exists) {
    breadcrumbs.push({
      __typename: 'File',
      _id: parent._id,
      folder: false,
      filename: 'Unknown',
      owner: null,
      url: null,
      adminLock: null,
      preventDownload: null,
      lastModified: null,
      lastUploaded: null,
      userCanSign: false,
      pinned: null,
      signingVersion: null,
      parent,
      lastUploadedBy: null,
      lastModifiedBy: null,
      workflow: null,
      signatures: null,
    });
  }

  const segments: Segment[] = [
    {
      _id: owner?._id ?? '',
      name: owner?.name ?? '',
      file: null,
      fileId: null,
      parentId: null,
      ownerId: owner?._id ?? '',
      ownerName: null,
      ownerType: rootOwnerType,
      folder: true,
      isRootOwner: true,
    },
    ...breadcrumbs.map((crumb) => ({
      _id: crumb._id,
      file: crumb,
      fileId: crumb._id,
      parentId: crumb.parent?._id ?? null,
      name: crumb.filename,
      folder: crumb.folder,
      ownerId: crumb?.owner?._id ?? null,
      ownerName: crumb?.owner?.name ?? null,
      ownerType: crumb?.owner?.__typename ?? 'Group',
    })),
  ];

  const lastSegment = segments[segments.length - 1];

  const { handleUploadRequestFromDrop } = useFileUploader({
    segment: lastSegment,
    setUploadingState,
    rootOwnerId: rootOwnerId ?? '',
  });

  const { getRootProps, isDragAccept } = useDropzone({
    onDrop: async (acceptedFiles) => {
      return handleUploadRequestFromDrop(acceptedFiles);
    },
    noClick: true,
  });

  const sharingGroups = groupsForSharing
    ? groupsForSharing.edges.map((edge) => edge.node)
    : [owner];

  const ownerPermissions = (relatedOwners ?? []).map((obj) => {
    return {
      _id: obj.owner._id,
      userCanEditFiles: obj.userCanEditFiles,
      userCanLockFiles: obj.userCanLockFiles,
      userCanPreventDownloadOfFiles: obj.userCanPreventDownloadOfFiles,
      userCanShareFiles: sharingGroups.length > 0,
    };
  });

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  });

  const sensors = useSensors(mouseSensor);

  return (
    <>
      {filesForOwner ? (
        <FileManagerUploadingBar
          filesForOwner={filesForOwner}
          uploadingState={uploadingState}
          setUploadingState={setUploadingState}
        />
      ) : null}
      <DndContext
        collisionDetection={pointerWithin}
        sensors={sensors}
        onDragEnd={(event) => {
          const { active, over } = event;
          const { current: activeData } = active.data ?? {};
          const { current: overData } = over?.data ?? {};

          const fileToMove = activeData?.file as
            FileRowFileFieldsFragment | undefined;
          const ownerCandidates = activeData?.ownerCandidates as
            OwnerCandidate[] | undefined;
          const oldOwnerId = fileToMove?.owner?._id;

          const newParent = overData?.file as
            FileRowFileFieldsFragment | null | undefined;
          const newOwnerId = overData?.ownerId as string | null;

          if (
            !fileToMove ||
            !ownerCandidates ||
            fileToMove?._id === newParent?._id ||
            !filesForOwner
          ) {
            return;
          }

          const isOwnerIdChanging = newOwnerId !== oldOwnerId;

          // Opens dialog to get confirmation if the owner and visibility is changing
          if (isOwnerIdChanging) {
            const ownerName = ownerCandidates.find(
              (candidate) => candidate._id === newOwnerId,
            )?.name;

            if (!ownerName) {
              return;
            }

            setChangeOwnerDialogOpen({
              file: fileToMove,
              parentId: newParent?._id,
              ownerId: newOwnerId,
              rootOwnerId: rootOwnerId ?? '',
              isChangingParent: true,
              isFolder: true,
              isMovingToRoot: newOwnerId === rootOwnerId,
              ownerName,
              open: true,
              onClose: () => setChangeOwnerDialogOpen(undefined),
            });
          } else {
            setConfirmMoveDialogOpen({
              rootOwnerId: rootOwnerId ?? '',
              rootOwnerName: owner?.name ?? '',
              file: fileToMove,
              parent: newParent ?? null,
              open: true,
              onClose: () => setConfirmMoveDialogOpen(undefined),
            });
          }
        }}
      >
        <div {...getRootProps()}>
          <FileManagerToolbar
            filesForOwner={filesForOwner}
            breadcrumbs={breadcrumbs}
            segments={segments}
            ownerPermissions={ownerPermissions}
            searchMode={searchMode}
            searchText={searchText}
            setSearchText={setSearchText}
            setSearchMode={setSearchMode}
            setUploadingState={setUploadingState}
          />
          <FileManagerContent
            filesForOwner={filesForOwner}
            loading={loading || !rootOwnerId}
            ownerPermissions={ownerPermissions}
            rootOwnerId={rootOwnerId ?? ''}
            searchText={searchText}
            searchMode={searchMode}
            sort={sort}
            allSelected={isDragAccept}
            setUploadingState={setUploadingState}
          />
        </div>
      </DndContext>
      {changeOwnerDialogOpen ? (
        <FileManagerChangeOwnerConfirmDialog {...changeOwnerDialogOpen} />
      ) : null}
      {confirmMoveDialogOpen ? (
        <ConfirmFileMoveDialog {...confirmMoveDialogOpen} />
      ) : null}
    </>
  );
};
